import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Modal,
  Pagination,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as d3 from 'd3';
import GoogleMapReact from 'google-map-react';
import { deepCopyObject, formatCurrency, getPercentage, mapOptions } from 'src/utils/functions';
import { GOOGLE_MAPS_API_KEY } from 'src/keys';
import { Pointer, TooltipCard } from 'src/App/HOC';
import { convertData, getIfReportTypeDisabled } from '../../../helper';
import CenterPointer from 'src/App/HOC/Pointer/CenterPointer';

import { reportPageStyles as styles } from '../styles';
import EditButton from '../../../UtilityComponents/EditButton';
import CloseIcon from '@mui/icons-material/Close';
import { COLOR_GREEN, COLOR_PRIMARY, COLOR_RED, exchangeRates, monthNames } from 'src/const';
import { COLOR_PRIMARY_1, COLOR_WARNING } from 'src/colors';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TooltipNew from '../../../UtilityComponents/TooltipNew';
import CottageRoundedIcon from '@mui/icons-material/CottageRounded';
import { UserReportAccessTypes } from '../../../propTypes';
import DisabledBlurCard from '../../../UtilityComponents/BlurCard';

const VisibilityIconStyle = {
  background: '#fff',
  boxShadow: '0 1px 10px -3px #0005',
  padding: '5px',
  borderRadius: '900px',
  transition: 'all 0.25s ease',
  '&:hover': {
    boxShadow: '0 3px 15px -3px #0007',
  },
};

const ListingDetailsComponent = ({
  isHeading,
  thumbnail,
  reveiwScoresRating,
  visibleReviewCount,
  occupancyRate,
  avgDailyRate,
  isMobile,
}: {
  isHeading?: boolean;
  thumbnail: string;
  reveiwScoresRating: number;
  visibleReviewCount: string;
  occupancyRate: number;
  avgDailyRate: string;
  isMobile: boolean;
}) => {
  return (
    <Grid
      container
      item
      xs={12}
      sx={{
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {isHeading && <Box sx={{ height: '60px' }} />}
      {!isHeading && (
        <>
          <Grid item xs={2}>
            <img src={thumbnail} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Grid>
          <Grid container item xs={10} sx={{ padding: '5px', gap: '20px' }}>
            <Grid item xs={12} sx={{ mt: '5px' }}>
              <Typography variant="body1">
                Review Rating:{' '}
                <span style={{ color: COLOR_PRIMARY, fontWeight: 'bold' }}>
                  {reveiwScoresRating}★
                </span>{' '}
                (
                <span style={{ color: COLOR_PRIMARY, fontWeight: 'bold' }}>
                  {visibleReviewCount}
                </span>{' '}
                ratings)
              </Typography>
            </Grid>
            <Grid container xs={12} flexDirection={'row'} sx={{ gap: '5px', mb: '5px' }}>
              <Typography variant="body1">
                Occupancy Rate:{' '}
                <span style={{ color: COLOR_PRIMARY, fontWeight: 'bold' }}>{occupancyRate}%</span>
              </Typography>
              <Typography variant="body1">
                Avg Daily Rate:{' '}
                <span style={{ color: COLOR_PRIMARY, fontWeight: 'bold' }}>{avgDailyRate}</span>
              </Typography>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};

const getRevenuePercentileStatus = (
  revenue: number,
  currentMonthPercentile: { 'top_1/3': number | null; 'top_2/3': number | null }
) => {
  if (currentMonthPercentile['top_1/3'] !== null && currentMonthPercentile['top_2/3'] !== null) {
    if (revenue >= currentMonthPercentile['top_1/3']) {
      return 'top_1/3';
    } else if (revenue >= currentMonthPercentile['top_2/3']) {
      return 'top_2/3';
    } else {
      return 'bottom_1/3';
    }
  } else {
    return 'missing';
  }
};

const setColorGradeOfMonthlyRevenue = (
  noOfBookingsLtmMonthly: { [key: string]: number | 'missing' },
  monthlyRevenueLtmList: { [key: string]: number },
  monthlyAvgOccupancyRateList: { [key: string]: number },
  monthlyAvgDailyRateList: { [key: string]: number },
  topPercentileMonthlyObject: {
    [key: string]: { 'top_1/3': number | null; 'top_2/3': number | null };
  }
) => {
  const noOfBookingColorGradeMonthly: {
    [key: string]: {
      isBarInactive: boolean;
      color: string;
      joinWithPrevious: boolean;
      joinWithNext: boolean;
      revenue: number;
      avgOccupancyRate: number;
      avgDailyRate: number;
    };
  } = {};

  Object.keys(monthlyRevenueLtmList).map(date => {
    const revenue = monthlyRevenueLtmList[date];
    const noOfBooking = noOfBookingsLtmMonthly[date];
    const avgOccupancyRate = monthlyAvgOccupancyRateList[date];
    const avgDailyRate = monthlyAvgDailyRateList[date];

    const revenuePercentileStatus = getRevenuePercentileStatus(
      revenue,
      topPercentileMonthlyObject[date]
    );

    const isBarInactive = noOfBooking == 0 || noOfBooking == 'missing';
    const color =
      isBarInactive || revenuePercentileStatus === 'missing'
        ? '#fff'
        : revenuePercentileStatus === 'top_1/3'
        ? COLOR_GREEN
        : revenuePercentileStatus === 'top_2/3'
        ? COLOR_RED
        : COLOR_WARNING;

    noOfBookingColorGradeMonthly[date] = {
      isBarInactive: isBarInactive,
      color: color,
      joinWithPrevious: false,
      joinWithNext: false,
      revenue: revenue,
      avgOccupancyRate: avgOccupancyRate,
      avgDailyRate: avgDailyRate,
    };
  });

  // Checking if the 2 adjacent bars should merge

  Object.keys(noOfBookingColorGradeMonthly).map((date, index) => {
    if (index > 0) {
      const previousDate = Object.keys(noOfBookingColorGradeMonthly)[index - 1];
      const currentDate = date;
      const previousColor = noOfBookingColorGradeMonthly[previousDate].color;
      const currentColor = noOfBookingColorGradeMonthly[currentDate].color;

      if (previousColor == currentColor) {
        noOfBookingColorGradeMonthly[currentDate].joinWithPrevious = true;
        noOfBookingColorGradeMonthly[previousDate].joinWithNext = true;
      }
    }
  });

  return noOfBookingColorGradeMonthly;
};

const ListingPerfromanceComponent = ({
  noOfBookingsLtmMonthly,
  monthlyRevenueLtmList,
  monthlyAvgOccupancyRateList,
  monthlyAvgDailyRateList,
  isHeading,
  isMobile,
  currency,
  topPercentileMonthlyObject,
  isLtmRevenueBarsDisabled,
  handleLoginOrPricingModalOpen,
  closeCompsEditModal,
}: {
  isHeading?: boolean;
  noOfBookingsLtmMonthly: { [key: string]: number };
  monthlyRevenueLtmList: { [key: string]: number };
  monthlyAvgOccupancyRateList: { [key: string]: number };
  monthlyAvgDailyRateList: { [key: string]: number };
  isMobile: boolean;
  currency: string;
  topPercentileMonthlyObject: {
    [key: string]: { 'top_1/3': number | null; 'top_2/3': number | null };
  };
  isLtmRevenueBarsDisabled?: boolean;
  handleLoginOrPricingModalOpen?: (
    state: 'open' | 'close',
    forceOpen?: 'payment' | null,
    estimatorSimplePricingModalViewMode?: 'pro' | 'expert' | 'scale'
  ) => void;
  closeCompsEditModal?: () => void;
}) => {
  const monthNameList: JSX.Element[] = [];

  const noOfBookingsMonthlyWitColorGrade = setColorGradeOfMonthlyRevenue(
    noOfBookingsLtmMonthly,
    monthlyRevenueLtmList,
    monthlyAvgOccupancyRateList,
    monthlyAvgDailyRateList,
    topPercentileMonthlyObject
  );

  if (isHeading) {
    Object.keys(noOfBookingsLtmMonthly).map(date => {
      const [yearStr, monthStr] = date.split('-');
      const monthName = monthNames[parseInt(monthStr) - 1].slice(0, 1).toUpperCase();
      const year = yearStr.slice(2, 4);
      monthNameList.push(
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: '5px' }}>
          <span style={{ fontSize: '14px' }}>{monthName}</span>
          <span style={{ fontSize: '10px', lineHeight: '10px' }}>{year}</span>
        </Box>
      );
    });
  }

  const blurStyle =
    !isHeading && isLtmRevenueBarsDisabled ? { pointerEvents: 'none', filter: 'blur(6px)' } : {};

  return (
    <>
      {isLtmRevenueBarsDisabled && !isHeading && (
        <DisabledBlurCard
          subscriptionType="Expert"
          onDisabledOptionClick={() => {
            handleLoginOrPricingModalOpen &&
              handleLoginOrPricingModalOpen('open', 'payment', 'expert');
            closeCompsEditModal && closeCompsEditModal();
          }}
        />
      )}
      <Grid
        container
        item
        xs={12}
        alignItems={'center'}
        justifyContent={'center'}
        sx={{
          ...(isMobile ? { padding: '10px' } : {}),
          ...blurStyle,
        }}
      >
        {Object.keys(noOfBookingsMonthlyWitColorGrade).map((date, index) => {
          const [yearStr, monthStr] = date.split('-');
          const monthName = monthNames[parseInt(monthStr) - 1];

          const isBarInactive = noOfBookingsMonthlyWitColorGrade[date].isBarInactive;
          const barColor = noOfBookingsMonthlyWitColorGrade[date].color;
          const joinWithPrevious = noOfBookingsMonthlyWitColorGrade[date].joinWithPrevious;
          const joinWithNext = noOfBookingsMonthlyWitColorGrade[date].joinWithNext;

          const revenue = noOfBookingsMonthlyWitColorGrade[date].revenue;
          const formattedRevenue =
            revenue && !isNaN(revenue)
              ? exchangeRates[currency].symbol + formatCurrency(String(revenue))
              : revenue;

          const occupancyRate = noOfBookingsMonthlyWitColorGrade[date].avgOccupancyRate;

          const avgDailyRate = noOfBookingsMonthlyWitColorGrade[date].avgDailyRate;
          const formattedAvgDailyRate =
            avgDailyRate && !isNaN(avgDailyRate)
              ? exchangeRates[currency].symbol + formatCurrency(String(avgDailyRate))
              : avgDailyRate;

          const RevenueBar = (
            <Grid
              item
              xs={1}
              sx={{
                ...(isHeading
                  ? {
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '0 !important',
                      background: '#fff',
                      borderRight: '1px solid #ccc',
                      fontWeight: 'bold',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                    }
                  : {
                      height: '20px',
                      borderRadius: '200px',
                      background: barColor,
                      ...(joinWithPrevious && {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      }),
                      ...(joinWithNext && { borderTopRightRadius: 0, borderBottomRightRadius: 0 }),
                    }),
              }}
            >
              {isHeading && (
                <Typography
                  variant="body1"
                  sx={{ color: COLOR_PRIMARY, fontWeight: 'bold', textAlign: 'center' }}
                >
                  {monthNameList[index]}
                </Typography>
              )}
            </Grid>
          );

          return isBarInactive || isHeading ? (
            RevenueBar
          ) : (
            <TooltipNew
              title={`${yearStr}, ${monthName} historical performance`}
              content={
                <>
                  <Divider sx={{ mb: '5px' }} />
                  <Typography variant="body1" sx={{ fontSize: '16px' }}>
                    Revenue: <span style={{ fontWeight: 600 }}>{formattedRevenue}</span>
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '16px' }}>
                    Occupancy Rate: <span style={{ fontWeight: 600 }}>{occupancyRate}%</span>
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '16px' }}>
                    Avg Daily Rate: <span style={{ fontWeight: 600 }}>{formattedAvgDailyRate}</span>
                  </Typography>
                </>
              }
            >
              {RevenueBar}
            </TooltipNew>
          );
        })}
      </Grid>
    </>
  );
};

const OverlayOnDisabeListing = () => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: '#0000',
        backdropFilter: 'saturate(0)',
      }}
    />
  );
};

const ListingComponents = ({
  comp,
  currency,
  isHeading,
  monthlyAvgOccupancyRateList,
  monthlyAvgDailyRateList,
  selectedComps,
  setSelectedComps,
  setMapCenter,
  isMobile,
  topPercentileMonthlyObject,
  isLtmRevenueBarsDisabled,
  isListingCardDisabled,
  isVisibilityToggleDisabled,
  handleLoginOrPricingModalOpen,
  closeCompsEditModal,
}: {
  comp: any;
  currency: string;
  monthlyAvgOccupancyRateList: { [key: string]: number };
  monthlyAvgDailyRateList: { [key: string]: number };
  isHeading?: boolean;
  selectedComps: { [key: string]: boolean };
  setSelectedComps: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  setMapCenter?: React.Dispatch<React.SetStateAction<{ lat: number; lng: number }>>;
  isMobile: boolean;
  topPercentileMonthlyObject: {
    [key: string]: { 'top_1/3': number | null; 'top_2/3': number | null };
  };
  isLtmRevenueBarsDisabled?: boolean;
  isListingCardDisabled?: boolean;
  isVisibilityToggleDisabled?: boolean;
  handleLoginOrPricingModalOpen?: (
    state: 'open' | 'close',
    forceOpen?: 'payment' | null,
    estimatorSimplePricingModalViewMode?: 'pro' | 'expert' | 'scale'
  ) => void;
  closeCompsEditModal?: () => void;
}) => {
  const listingId = comp.listingID;
  const thumbnail = comp.thumbnail_url;
  const reveiwScoresRating = comp.reveiw_scores_rating;
  const visibleReviewCount = formatCurrency(String(comp.visible_review_count));
  const occupancyRate = comp.avg_occupancy_rate_ltm;
  const avgDailyRate =
    exchangeRates[currency].symbol + formatCurrency(String(comp.avg_booked_daily_rate_ltm));

  const monthlyRevenueLtmList = comp.revenue_ltm_monthly;
  const noOfBookingsLtmMonthly = comp.no_of_bookings_ltm_monthly;

  const handleVisibilityClick = () => {
    setSelectedComps({
      ...selectedComps,
      [listingId]: !selectedComps[listingId],
    });
  };

  const handleSetCurrentCoord = () => {
    setMapCenter && setMapCenter({ lat: comp.latitude, lng: comp.longitude });
  };

  const blurStyle = isListingCardDisabled ? { pointerEvents: 'none', filter: 'blur(6px)' } : {};

  return (
    <Grid
      container
      item
      justifyContent={'flex-start'}
      spacing={2}
      xs={12}
      onMouseOver={() => !isListingCardDisabled && handleSetCurrentCoord()}
      sx={{
        mb: '20px',
        position: 'relative',
        ...(isMobile ? { boxShadow: '0 4px 10px -5px #0004', borderRadius: '12px' } : {}),
        ...(isHeading && !isMobile
          ? { position: 'sticky', top: '-40px', zIndex: 900, background: '#fff' }
          : {}),
      }}
    >
      {/* Left: Listing Details */}
      <Grid container item xs={isMobile ? 12 : 5} sx={{ position: 'relative', ...blurStyle }}>
        <ListingDetailsComponent
          isHeading={isHeading}
          reveiwScoresRating={reveiwScoresRating}
          visibleReviewCount={visibleReviewCount}
          occupancyRate={occupancyRate}
          avgDailyRate={avgDailyRate}
          thumbnail={thumbnail}
          isMobile={isMobile}
        />
        {!selectedComps[listingId] && <OverlayOnDisabeListing />}
      </Grid>
      {/* Right: Listing Performance */}
      <Grid container item xs={isMobile ? 12 : 6} spacing={2} sx={{ position: 'relative' }}>
        <>
          {isMobile && (
            <ListingPerfromanceComponent
              isHeading={true}
              noOfBookingsLtmMonthly={noOfBookingsLtmMonthly}
              monthlyRevenueLtmList={monthlyRevenueLtmList}
              monthlyAvgOccupancyRateList={monthlyAvgOccupancyRateList}
              monthlyAvgDailyRateList={monthlyAvgDailyRateList}
              isMobile={true}
              currency={currency}
              topPercentileMonthlyObject={topPercentileMonthlyObject}
              // isLtmRevenueBarsDisabled={isLtmRevenueBarsDisabled}
            />
          )}
          <ListingPerfromanceComponent
            isHeading={isHeading}
            noOfBookingsLtmMonthly={noOfBookingsLtmMonthly}
            monthlyRevenueLtmList={monthlyRevenueLtmList}
            monthlyAvgOccupancyRateList={monthlyAvgOccupancyRateList}
            monthlyAvgDailyRateList={monthlyAvgDailyRateList}
            isMobile={isMobile}
            currency={currency}
            topPercentileMonthlyObject={topPercentileMonthlyObject}
            isLtmRevenueBarsDisabled={isLtmRevenueBarsDisabled}
            handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
            closeCompsEditModal={closeCompsEditModal}
          />
          {!isHeading && !selectedComps[listingId] && <OverlayOnDisabeListing />}
        </>
      </Grid>
      <Grid
        item
        xs={isMobile ? 12 : 1}
        onClick={handleVisibilityClick}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 !important',
          marginTop: isMobile ? '0px !important' : '16px !important',
          marginBottom: isMobile ? '15px !important' : '0 !important',
          cursor: 'pointer',
          color: selectedComps[listingId] ? COLOR_GREEN : COLOR_RED,
          ...(isVisibilityToggleDisabled && { pointerEvents: 'none', opacity: 0.5 }),
        }}
      >
        {!isHeading &&
          (selectedComps[listingId] ? (
            <VisibilityIcon sx={VisibilityIconStyle} />
          ) : (
            <VisibilityOffIcon sx={VisibilityIconStyle} />
          ))}
      </Grid>
    </Grid>
  );
};

const sortSelectedCompsFirst = (comps: any) => {
  const selectedComps = comps
    .filter((comp: any) => comp.is_selected == 1)
    .map((comp: any) => comp.listingID);
  const unselectedComps = comps
    .filter((comp: any) => comp.is_selected == 0)
    .map((comp: any) => comp.listingID);
  return [...selectedComps, ...unselectedComps];
};

const getSortedCompsWindow = (
  comps: any,
  sortedComps: string[],
  startIndex: number,
  endIndex: number
) => {
  return sortedComps
    .slice(startIndex, endIndex)
    .map((compId: string) => comps.find((comp: any) => comp.listingID == compId));
};

const getAvgAllCompsMonthlyRevenueList = (comps: any) => {
  // INITIALIZING THE DATE OBJECT
  const dateObj = Object.keys(comps[0].revenue_ltm_monthly).reduce(
    (acc: { [key: string]: number[] }, date: string) => {
      acc[date] = [];
      return acc;
    },
    {}
  );

  const dateObjectCntainingPercentile = Object.keys(comps[0].revenue_ltm_monthly).reduce(
    (
      acc: { [key: string]: { 'top_1/3': number | null; 'top_2/3': number | null } },
      date: string
    ) => {
      acc[date] = { 'top_1/3': null, 'top_2/3': null };
      return acc;
    },
    {}
  );

  // POPULATING THE DATE OBJECT
  comps.map((comp: any) => {
    const revenueObj = comp.revenue_ltm_monthly;
    Object.keys(revenueObj).map((date: string) => {
      if (revenueObj[date] !== 'missing' && revenueObj[date] !== 'inactive') {
        dateObj[date].push(revenueObj[date]);
      }
    });
  });

  // CALCULATING THE Percentile
  Object.keys(dateObj).map((date: string) => {
    const revenueList = dateObj[date];

    const sortedRevenueList = revenueList.sort((a, b) => a - b);
    const top1_3 = d3.quantile(sortedRevenueList, '0.66');
    const top2_3 = d3.quantile(sortedRevenueList, '0.33');

    dateObjectCntainingPercentile[date] = { 'top_1/3': top1_3, 'top_2/3': top2_3 };
  });

  return dateObjectCntainingPercentile;
};

const CompsEditModal = ({
  reportId,
  comps,
  currency,
  isCompsEditModalOpen,
  setIsCompsEditModalOpen,
  handleReport,
  centerPin,
  trackEditPerformed,
  reportAccessType,
  handleLoginOrPricingModalOpen,
}: {
  reportId: string;
  comps: any;
  currency: string;
  isCompsEditModalOpen: boolean;
  setIsCompsEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleReport: (method: any, requestBody: null | any, id?: string) => Promise<void>;
  centerPin: () => JSX.Element;
  trackEditPerformed: (editType: any) => void;
  reportAccessType: UserReportAccessTypes;
  handleLoginOrPricingModalOpen: (
    state: 'open' | 'close',
    forceOpen?: 'payment' | null,
    estimatorSimplePricingModalViewMode?: 'pro' | 'expert' | 'scale'
  ) => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isLtmRevenueBarsDisabled: boolean = getIfReportTypeDisabled(
    reportAccessType,
    'CompsLtmRevenueBars'
  );
  const isFetchMoreCompsButtonDisabled: boolean = getIfReportTypeDisabled(
    reportAccessType,
    'CompsFilterFetchMoreListingsButton'
  );

  const compsDisplayOrder = sortSelectedCompsFirst(comps);

  const [selectedComps, setSelectedComps] = useState<{ [key: string]: boolean }>(
    comps.reduce((acc: { [key: string]: boolean }, comp: any) => {
      acc[comp.listingID] = comp.is_selected == 1 ? true : false;
      return acc;
    }, {})
  );

  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({
    lat: comps[0].latitude,
    lng: comps[0].longitude,
  });

  const [currentCompsSelectionWindow, setCurrentCompsSelectionWindow] = useState<[number, number]>([
    0,
    10,
  ]);

  const handleSetCompsSelectionWindow = () => {
    setCurrentCompsSelectionWindow(prev => [prev[0], Math.min(40, (prev[1] / 10 + 1) * 10)]);
  };

  const handleCloseEditModal = () => {
    setIsCompsEditModalOpen(false);
  };

  const handleCompsUpdate = () => {
    const parsedSelectedComps = Object.keys(selectedComps).reduce(
      (acc: { [key: string]: number }, compId: string) => {
        acc[compId] = selectedComps[compId] ? 1 : 0;
        return acc;
      },
      {}
    );

    const updateCompsObject = {
      update_type: 'comps',
      id: reportId,
      comps: parsedSelectedComps,
    };

    handleReport('UPDATE', updateCompsObject);
  };

  const topPercentileMonthlyObject = getAvgAllCompsMonthlyRevenueList(comps);

  const totalNumberOfCompsSelected: number = Object.keys(selectedComps).filter(
    (compId: string) => selectedComps[compId]
  ).length;

  const closeCompsEditModal = () => {
    setIsCompsEditModalOpen(false);
  };

  return (
    <Modal open={isCompsEditModalOpen} onClose={handleCloseEditModal}>
      <Box
        sx={{
          ...styles.filterModalBodyRoot,
          width: `clamp(300px, '90vw', 1400px)`,
          ...(isMobile && { display: 'flex', flexDirection: 'column', alignItems: 'center' }),
        }}
      >
        <IconButton
          onClick={handleCloseEditModal}
          sx={{ ...styles.filterModalCloseIcon, zIndex: 9999 }}
        >
          <CloseIcon />
        </IconButton>

        <Grid container xs={12} justifyContent={'space-between'} spacing={2} sx={{ mt: '0px' }}>
          {isMobile && (
            <Grid item xs={0} sx={{ mb: '10px' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '20px' }}>
                Edit Comparables
              </Typography>
            </Grid>
          )}
          {/* LEFT - MINI MAP */}
          {!isMobile && (
            <Grid container item xs={3} sx={{ height: '280px', position: 'sticky', top: 0 }}>
              <Grid item xs={0} sx={{ mb: '10px' }}>
                {/* <Typography variant="body1" sx={styles.filterGroupHeadingTextColor}> */}
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '20px' }}>
                  Edit Comparables
                </Typography>
              </Grid>

              <GoogleMapReact
                bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
                center={{ lat: mapCenter.lat, lng: mapCenter.lng }}
                defaultZoom={15}
              >
                {centerPin()}
                <CenterPointer
                  lat={mapCenter.lat}
                  lng={mapCenter.lng}
                  CustomIcon={CottageRoundedIcon}
                />
              </GoogleMapReact>
            </Grid>
          )}
          {/* RIGHT COMPS */}
          <Grid container item xs={isMobile ? 12 : 9}>
            <Grid item xs={12}>
              {/* In Card Header for mobile view */}
              {!isMobile && (
                <ListingComponents
                  comp={comps[0]}
                  isHeading={true}
                  currency={currency}
                  selectedComps={selectedComps}
                  setSelectedComps={setSelectedComps}
                  isMobile={isMobile}
                  monthlyAvgOccupancyRateList={comps[0].occupancy_rate_ltm_monthly}
                  monthlyAvgDailyRateList={comps[0].booked_daily_rate_ltm_monthly}
                  topPercentileMonthlyObject={topPercentileMonthlyObject}
                  isLtmRevenueBarsDisabled={isLtmRevenueBarsDisabled}
                />
              )}
              {getSortedCompsWindow(
                comps,
                compsDisplayOrder,
                currentCompsSelectionWindow[0],
                currentCompsSelectionWindow[1]
              ).map((comp, index) => {
                return (
                  <>
                    <ListingComponents
                      key={'edit-comps' + index}
                      comp={comp}
                      currency={currency}
                      selectedComps={selectedComps}
                      setSelectedComps={setSelectedComps}
                      setMapCenter={setMapCenter}
                      isMobile={isMobile}
                      monthlyAvgOccupancyRateList={comp.occupancy_rate_ltm_monthly}
                      monthlyAvgDailyRateList={comp.booked_daily_rate_ltm_monthly}
                      topPercentileMonthlyObject={topPercentileMonthlyObject}
                      isLtmRevenueBarsDisabled={isLtmRevenueBarsDisabled}
                      isListingCardDisabled={getIfReportTypeDisabled(
                        reportAccessType,
                        'CompsFilterListing',
                        index
                      )}
                      isVisibilityToggleDisabled={getIfReportTypeDisabled(
                        reportAccessType,
                        'CompsFilterListingVisibilityToggleSwitch'
                      )}
                      handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
                      closeCompsEditModal={closeCompsEditModal}
                    />
                    {!isMobile && (
                      <Grid container item xs={12} sx={{ mt: '-10px', mb: '10px' }}>
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                      </Grid>
                    )}
                  </>
                );
              })}
            </Grid>
            {currentCompsSelectionWindow[1] < 40 && (
              <Grid container justifyContent={'center'} xs={12}>
                <Button
                  variant="outlined"
                  disabled={isFetchMoreCompsButtonDisabled}
                  onClick={() => !isFetchMoreCompsButtonDisabled && handleSetCompsSelectionWindow()}
                >
                  Review More Comps
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container xs={12} justifyContent={'flex-end'} sx={{ mt: '20px' }}>
          <TooltipNew
            arrow={false}
            placement="left"
            title={'Please select at least 10 comps.'}
            content={''}
            disable={totalNumberOfCompsSelected < 10}
          >
            <Button
              onClick={() => {
                trackEditPerformed('map');
                totalNumberOfCompsSelected >= 10 && handleCompsUpdate();
              }}
              sx={styles.actionSectionButton}
            >
              Apply
            </Button>
          </TooltipNew>
        </Grid>
      </Box>
    </Modal>
  );
};

const ReportPageMapSection = ({
  reportId,
  lat,
  lng,
  radius,
  comps,
  currency,
  handleReport,
  isUserReportOwner,
  trackEditOpened,
  trackEditPerformed,
  isOnboardingComplete,
  isUserSubscribed,
  handleLoginOrPricingModalOpen,
  reportAccessType,
}: {
  reportId: string;
  lat: number;
  lng: number;
  radius: number;
  comps: any;
  currency: string;
  handleReport: (method: any, requestBody: null | any, id?: string) => Promise<void>;
  isUserReportOwner: boolean;
  trackEditOpened: (editType: any) => void;
  trackEditPerformed: (editType: any) => void;
  isOnboardingComplete: boolean;
  isUserSubscribed: boolean;
  handleLoginOrPricingModalOpen: (
    state: 'open' | 'close',
    forceOpen?: 'payment' | null,
    estimatorSimplePricingModalViewMode?: 'pro' | 'expert' | 'scale'
  ) => void;
  reportAccessType: UserReportAccessTypes;
}) => {
  const [mapRef, setMapRef] = useState<{
    map: any;
    maps: any;
  } | null>(null);
  const [searchedRegionBoundry, setSearchedRegionBoundry] = useState<any>(null);
  const [isCompsEditModalOpen, setIsCompsEditModalOpen] = useState<boolean>(false);

  const apiIsLoaded = (map, maps) => {
    setMapRef({ map, maps });
    makeCircle(map, maps);
  };

  const makeCircle = (map, maps) => {
    if (!maps) {
      return;
    }

    const highlightedEstimatorRegion = new maps.Circle({
      strokeColor: '#52C879',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#4aa59a',
      fillOpacity: 0.12,
      center: { lat, lng },
      radius: radius,
    });
    highlightedEstimatorRegion.setMap(map);

    setSearchedRegionBoundry(highlightedEstimatorRegion);

    map.fitBounds(highlightedEstimatorRegion.getBounds());
  };

  const markers = useMemo(() => {
    if (comps) {
      const parsedComps = comps
        .filter(item => item.is_selected == 1)
        .map((item, index: number) => convertData(item, currency));

      return parsedComps
        .map((item, index: number) => {
          const isCurrentListingDisabled = getIfReportTypeDisabled(
            reportAccessType,
            'ListingMarker',
            index
          );
          const isHistoricalDataHidden = getIfReportTypeDisabled(
            reportAccessType,
            'ListingMarkerCardLTMHistoryVisibility'
          );

          return (
            <Pointer
              key={index}
              lat={item.latitude}
              lng={item.longitude}
              data={item}
              setSubscriptionModalOpen={() => {}}
              listings={parsedComps}
              setListings={() => {}}
              isDisabled={isCurrentListingDisabled}
              currency={currency}
              canDelete={true}
              subscriptionModalOpen={false}
              multipleBookingMarkersCallLoading={false}
              showRevenuePotential={true}
              isEstimator={true}
              deletedEstimatorPointer={false}
              handleOnSampleClick={() => {}}
              hiddenListingIds={[]}
              addHiddenListingId={() => {}}
              removeHiddenListingId={() => {}}
              isEstimatorLocked={false}
              isHistoricalDataHidden={isHistoricalDataHidden}
              onEstimatorLockedMarkerClick={() =>
                isUserReportOwner &&
                isCurrentListingDisabled &&
                handleLoginOrPricingModalOpen('open', 'payment', 'pro')
              }
              onEstimatorLockedMarkerHistoricalDataClick={() =>
                isUserReportOwner &&
                isHistoricalDataHidden &&
                handleLoginOrPricingModalOpen('open', 'payment', 'expert')
              }
            />
          );
        })
        .reverse();
    }
    return;
  }, []);

  const centerPin = () => {
    return <CenterPointer lat={lat} lng={lng} />;
  };

  return (
    <>
      <Grid
        item
        container
        id="estimator-map-container"
        alignItems="flex-start"
        justifyContent="center"
        sx={{ ...styles.paper, position: 'relative', paddingTop: '60px', height: '400px' }}
      >
        <Box sx={{ position: 'absolute', top: '10px', right: '10px' }}>
          <EditButton
            isDisabled={!isUserReportOwner}
            actionOnClick={() => {
              setIsCompsEditModalOpen(true);
              trackEditOpened('map');
            }}
            isLocked={false}
          />
        </Box>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: GOOGLE_MAPS_API_KEY,
            libraries: ['drawing', 'places', 'visualization'],
            id: 'estimator-map',
          }}
          center={{ lat, lng }}
          options={mapOptions}
          zoom={12}
          resetBoundsOnResize
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps)}
        >
          {centerPin()}
          {markers}
        </GoogleMapReact>
      </Grid>
      <CompsEditModal
        reportId={reportId}
        comps={comps}
        currency={currency}
        isCompsEditModalOpen={isCompsEditModalOpen}
        setIsCompsEditModalOpen={setIsCompsEditModalOpen}
        handleReport={handleReport}
        centerPin={centerPin}
        trackEditPerformed={trackEditPerformed}
        reportAccessType={reportAccessType}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
      />
    </>
  );
};

export default ReportPageMapSection;
